import React, { useContext, useMemo, useState, useRef, useEffect, useCallback } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import Slider from "react-slick";
import slugify from "@sindresorhus/slugify";
import classNames from "classnames";
import Parser from "html-react-parser";
import { Link } from "./link";
import YoutubeContext from "../context/youtube";
import PlayDark from "../icons/play-dark.svg";
import CartIcon from "../icons/carrello.svg";
import sortPosition from "../utility/sort-position";
import { queryProductsDetails } from "../utility/magento";
import ListingItemPdp from "./listing-item-pdp";
import CartContext from "../context/cart";
import DynamicAsGallery from "./dynamic-as-gallery";

const Image = ({ image, alt, ...props }) => {
  if (!getImage(image)) {
    return <img src={image.publicURL} alt={alt} {...props} />;
  }
  return <GatsbyImage image={getImage(image)} {...props} />;
};

const formatList = (module) => {
  const { sectionTitleFieldSet, moduleLook } = module;
  const list =
    moduleLook?.[0] === "Inline"
      ? sectionTitleFieldSet
        ? sectionTitleFieldSet.slice(0, 4)
        : sectionTitleFieldSet
      : sectionTitleFieldSet;

  return list
    ? list
        .map((item) => ({
          title: item.sectionTitle,
          realSubTitle: item.subTitle ? item.subTitle : "",
          subtitle: item.sectionText,
          image:
            item.sectionImageFieldSet &&
            item.sectionImageFieldSet?.some((elem) => elem?.sectionImage?.node)
              ? item.sectionImageFieldSet
                  ?.filter((elem) => elem?.sectionImage?.node)
                  .map((img) => ({
                    node: img.sectionImage.node,
                    nodeFull: img.sectionImage.nodeFull,
                    alt: img ? img?.altImage : "",
                  }))
              : null,
          cta:
            item.labelFieldSet &&
            (!!item.labelFieldSet.length
              ? item.labelFieldSet.map((cta) => ({
                  linkLabel: cta.label,
                  link: cta.link,
                  positionCTA:
                    cta.positionCta && cta.positionCta?.[0] ? cta.positionCta?.[0] : "bottom",
                  btnColor: cta.color ? cta.color?.[0] : "",
                }))
              : {
                  link: item.labelFieldSet ? item.labelFieldSet.link : null,
                  linkLabel: item.labelFieldSet.label,
                  positionCTA: "bottom",
                  btnColor: "Black",
                }),
          youtube: item.video ? item.video.split("/").reverse()[0] : null,
          icon: item.fieldsGroup,
          skuForWashPass: item?.skuForWashPass,
          position: parseInt(item?.weightSlide?.[0]),
          ratePlanId: item?.ratePlanId,
          unaTantum: item?.unaTantum,
          monthlyFee: item?.monthlyFee,
          extraWashFee: item?.extraWashFee,
          includedWashes: item?.includedWashes,
          iconBabel: item?.iconBabel,
          logoImage: item.logoImage,
          isFamily: item?.isFamily,
        }))
        .sort((a, b) => {
          const posA = a.position;
          const posB = b.position;
          return !isNaN(posA) && !isNaN(posB)
            ? posA - posB
            : isNaN(posA) && isNaN(posB)
            ? 0
            : isNaN(posA)
            ? 1
            : -1;
        })
    : list;
};

const settings = {
  dots: true,
  infinite: false,
  autoplay: false,
  arrows: false,
  swipe: true,
  slidesToShow: 1,
};

const StandardItem = ({
  item,
  md = 6,
  lg,
  showVideoModal,
  labelManagerLabels,
  config,
  isProduct,
  origin,
  dataZuora,
}) => {
  const { addProduct } = useContext(CartContext);
  const currency = config?.currency;
  const addItemToCart = useCallback(
    async (e) => {
      const normalizedProductIds = [];
      try {
        const productId = e.currentTarget.name;
        normalizedProductIds.push(productId);
        const [items = {}] = await queryProductsDetails(normalizedProductIds, currency);
        origin === "code-validator" ? addProduct(items, null, origin) : addProduct(items);
      } catch (err) {
        console.error("Error to retrive product : ", err);
      }
    },
    [addProduct, origin, currency]
  );

  const ratePlanFiltered = dataZuora
    ? dataZuora?.data?.data?.ratePlanGroups.filter(
        (ratePlanGroup) => ratePlanGroup?.[0]?.id === item?.ratePlanId
      )
    : null;

  // const additionalPrice = ratePlanFiltered?.[0]?.[0]?.productRatePlanCharges?.find(
  //   (productRatePlanCharge) =>
  //     productRatePlanCharge?.model === "FlatFee" &&
  //     productRatePlanCharge?.type === "OneTime" &&
  //     productRatePlanCharge?.name.includes("Contributo (una tantum)")
  // );

  const extraWashPrice = ratePlanFiltered?.[0]?.[0]?.productRatePlanCharges?.find(
    (productRatePlanCharge) =>
      productRatePlanCharge?.model === "PerUnit" &&
      productRatePlanCharge?.type === "Usage" &&
      productRatePlanCharge?.name.includes("Per ExtraWash Fee -")
  );

  const monthlyFee = ratePlanFiltered?.[0]?.[0]?.productRatePlanCharges?.find(
    (productRatePlanCharge) =>
      productRatePlanCharge?.model === "FlatFee" &&
      productRatePlanCharge?.type === "Recurring" &&
      (productRatePlanCharge?.name.includes("Subscription  monthly fee -") ||
        productRatePlanCharge?.name.includes("Monthly  Fee"))
  );

  const subtitleWashpass = item?.subtitle
    ? item?.subtitle
        .replace(
          "NUMBER_WASHES",
          ratePlanFiltered?.[0]?.[0]?.IncludedWashes__c
            ? ratePlanFiltered?.[0]?.[0].IncludedWashes__c
            : item?.includedWashes
            ? item.includedWashes
            : ""
        )
        // .replace(
        //   "ADDITIONAL_PRICE",
        //   additionalPrice?.pricing?.[0]?.price && additionalPrice?.pricing?.[0]?.currency
        //     ? `${additionalPrice.pricing?.[0].price} ${additionalPrice.pricing?.[0].currency}`
        //     : item?.unaTantum
        //     ? item.unaTantum
        //     : ""
        // )
        .replace(
          "EXTRA_WASH_PRICE",
          extraWashPrice?.pricing?.[0]?.price && extraWashPrice?.pricing?.[0]?.currency
            ? `${extraWashPrice.pricing?.[0].price} ${extraWashPrice.pricing?.[0].currency}`
            : item?.extraWashFee
            ? item.extraWashFee
            : ""
        )
    : null;

  const realSubTitleWashpass = item?.realSubTitle
    ? item?.realSubTitle.replace(
        "MONTHLY_FEE",
        monthlyFee?.pricing?.[0]?.price && monthlyFee?.pricing?.[0]?.currency
          ? `${monthlyFee.pricing?.[0].price} ${monthlyFee.pricing?.[0].currency}`
          : item?.monthlyFee
          ? item.monthlyFee
          : ""
      )
    : null;

  const titleWashpass = ratePlanFiltered?.[0]?.[0]?.productName
    ? ratePlanFiltered?.[0]?.[0].productName.split("-")
    : null;

  return (
    <div className={`col-md-${md}${lg ? ` col-lg-${lg}` : ""} text-center`}>
      <div className="items-grid__box">
        {(item.image || item.icon?.icon) && (
          <div className="items-grid__img">
            {item.icon?.icon ? (
              <div className="items-grid__icon">
                <img
                  src={item.icon.icon.node.publicURL}
                  style={{
                    width: item.icon?.width && `${item.icon?.width}px`,
                    height: item.icon?.heigth && `${item.icon?.heigth}px`,
                  }}
                  alt={`icona box ${item.title}`}
                />
              </div>
            ) : (
              !!item.image?.length && (
                <>
                  <Slider {...settings} className="slider--items-grid">
                    {item.image?.map((img, i) => (
                      <React.Fragment key={i}>
                        {typeof img.node === "string" ? (
                          <img className="w-100" src={img.node} alt={img.alt} />
                        ) : (
                          <Image className="img-slider" image={img.nodeFull} alt={img.alt} />
                        )}
                        {(!!item.image?.length || item.icon || item?.logoImage?.[0]?.node) &&
                          item.youtube &&
                          i === 0 && (
                            <button
                              className="btn-play btn-play--center"
                              aria-label="Play"
                              name={item.youtube}
                              onClick={showVideoModal}
                            >
                              <PlayDark />
                            </button>
                          )}
                      </React.Fragment>
                    ))}
                  </Slider>
                  {item.iconBabel && (
                    <div className="items-grid__icon">
                      <img
                        src={item.iconBabel}
                        style={{
                          width: item.iconBabel?.width,
                          height: item.iconBabel?.heigth,
                        }}
                        alt={`icona box ${item.title}`}
                      />
                    </div>
                  )}
                </>
              )
            )}
          </div>
        )}
        {item.title &&
          (isProduct ? (
            <h3 className="items-grid__item-title">
              {origin === "code-validator" && item.skuForWashPass && titleWashpass?.[1]
                ? titleWashpass?.[1].replace(/\s/g, "")
                : item.title}
            </h3>
          ) : (
            <div className="items-grid__item-title">
              {" "}
              {origin === "code-validator" && item.skuForWashPass && titleWashpass?.[1]
                ? titleWashpass?.[1].replace(/\s/g, "")
                : item.title}
            </div>
          ))}
        {item.realSubTitle && (
          <>
            {origin === "code-validator" && item.skuForWashPass ? (
              <div
                className="items-grid__item-realsubtitle font-light"
                dangerouslySetInnerHTML={{ __html: realSubTitleWashpass }}
              ></div>
            ) : (
              <div
                className="items-grid__item-realsubtitle font-light"
                dangerouslySetInnerHTML={{ __html: item.realSubTitle }}
              ></div>
            )}
          </>
        )}
        {item.cta && !!item.cta.length
          ? item.cta.map(
              (cta, i) =>
                (cta.positionCTA === "top" || cta.positionCTA === "all") &&
                (item.skuForWashPass && !cta.link ? (
                  <div className="mt-4" key={i}>
                    <button
                      className={classNames(
                        `btn-link`,
                        cta.btnColor === "White" && "btn-link--white",
                        cta.btnColor === "Blu" && "btn-link--blue",
                        cta.btnColor === "Black" && "btn-link--black"
                      )}
                      name={item.skuForWashPass}
                      onClick={addItemToCart}
                    >
                      {cta.linkLabel ? (
                        cta.linkLabel
                      ) : (
                        <>
                          <CartIcon className="icon-svg-detail" /> {labelManagerLabels.addToCart}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  cta.link && (
                    <div className="mt-4" key={i}>
                      <Link
                        to={cta.link}
                        className={classNames(
                          `btn-link`,
                          cta.btnColor === "White" && "btn-link--white",
                          cta.btnColor === "Blu" && "btn-link--blue",
                          cta.btnColor === "Black" && "btn-link--black"
                        )}
                      >
                        {cta.linkLabel || labelManagerLabels.discoverMore}
                      </Link>
                    </div>
                  )
                ))
            )
          : null}
        {item.subtitle && (
          <>
            {origin === "code-validator" && item.skuForWashPass ? (
              <div
                className="items-grid__item-subtitle font-light"
                dangerouslySetInnerHTML={{ __html: subtitleWashpass }}
              ></div>
            ) : (
              <div
                className="items-grid__item-subtitle font-light"
                dangerouslySetInnerHTML={{ __html: item.subtitle }}
              ></div>
            )}
          </>
        )}
        {item.cta &&
          (!!item.cta.length
            ? item.cta.map(
                (cta, i) =>
                  (cta.positionCTA === "bottom" || cta.positionCTA === "all") &&
                  (item.skuForWashPass && !cta.link ? (
                    <div className="mt-4" key={i}>
                      <button
                        className={classNames(
                          `btn-link`,
                          cta.btnColor === "White" && "btn-link--white",
                          cta.btnColor === "Blu" && "btn-link--blue",
                          cta.btnColor === "Black" && "btn-link--black"
                        )}
                        name={item.skuForWashPass}
                        onClick={addItemToCart}
                      >
                        {cta.linkLabel ? (
                          cta.linkLabel
                        ) : (
                          <>
                            <CartIcon className="icon-svg-detail" /> {labelManagerLabels.addToCart}
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    cta.link && (
                      <div className="mt-4" key={i}>
                        <Link
                          to={cta.link}
                          className={classNames(
                            `btn-link`,
                            cta.btnColor === "White" && "btn-link--white",
                            cta.btnColor === "Blu" && "btn-link--blue",
                            cta.btnColor === "Black" && "btn-link--black"
                          )}
                        >
                          {cta.linkLabel || labelManagerLabels.discoverMore}
                        </Link>
                      </div>
                    )
                  ))
              )
            : item.cta.link && (
                <div className="mt-4">
                  <Link to={item.cta.link} className="btn-link btn-link--black">
                    {item.cta.linkLabel || labelManagerLabels.discoverMore}
                  </Link>
                </div>
              ))}
      </div>
    </div>
  );
};

const ListingItem = ({ item, showVideoModal, labelManagerLabels, addItemToCart, type }) => (
  <div className={type === "Tripletta with image position" ? "col-md-12" : "col-md-6"}>
    <div className="items-grid__item-listing">
      <div className="row py-4">
        {(item.image || item.icon?.icon) && (
          <div
            className={classNames({
              "col-sm-4": item.icon?.icon,
              "col-sm-6": !item.icon?.icon,
            })}
          >
            <div className="position-relative">
              {!!item.image?.length && (
                <Slider {...settings}>
                  {item.image.map((img, i) => (
                    <React.Fragment key={i}>
                      {typeof img.node === "string" ? (
                        <img className="w-100" src={img.node} alt={img.alt} />
                      ) : (
                        <Image className="" image={img.node} alt={img.alt} />
                      )}
                    </React.Fragment>
                  ))}
                </Slider>
              )}
              {(!!item.image?.length || item.icon || item?.logoImage?.[0]?.node) && item.youtube && (
                <button
                  className="btn-play btn-play--center"
                  aria-label="Play"
                  name={item.youtube}
                  onClick={showVideoModal}
                >
                  <PlayDark />
                </button>
              )}
              {item.icon?.icon && (
                <div className="items-grid__icon">
                  <img
                    src={item.icon.icon.node.publicURL}
                    style={{
                      width: item.icon?.width && `${item.icon?.width}px`,
                      height: item.icon?.heigth && `${item.icon?.heigth}px`,
                    }}
                    alt={`icona box ${item.title}`}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className={classNames(
            "text-center text-sm-left d-flex flex-column justify-content-center align-items-center align-items-md-start",
            {
              "col-sm-8": item?.icon?.icon,
              "col-sm-6": !item?.icon?.icon,
            }
          )}
        >
          {item.title && <h3 className="items-grid__item-title mt-3 mb-2 mt-sm-0">{item.title}</h3>}
          {item.realSubTitle && (
            <div
              className="items-grid__item-realsubtitle font-light"
              dangerouslySetInnerHTML={{ __html: item.realSubTitle }}
            ></div>
          )}

          {item.cta &&
            !!item.cta.length &&
            item.cta.map(
              (cta) =>
                (cta.positionCTA === "top" || cta.positionCTA === "all") &&
                (item.skuForWashPass && !cta.link ? (
                  <div className="mt-4">
                    <button
                      className={classNames(
                        `btn-link`,
                        cta.btnColor === "White" && "btn-link--white",
                        cta.btnColor === "Blu" && "btn-link--blue",
                        cta.btnColor === "Black" && "btn-link--black"
                      )}
                      name={item.skuForWashPass}
                      onClick={addItemToCart}
                    >
                      {cta.linkLabel ? (
                        cta.linkLabel
                      ) : (
                        <>
                          <CartIcon className="icon-svg-detail" /> {labelManagerLabels.addToCart}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  cta.link && (
                    <div className="mt-4">
                      <Link
                        to={cta.link}
                        className={classNames(
                          `btn-link`,
                          cta.btnColor === "White" && "btn-link--white",
                          cta.btnColor === "Blu" && "btn-link--blue",
                          cta.btnColor === "Black" && "btn-link--black"
                        )}
                      >
                        {cta.linkLabel || labelManagerLabels.discoverMore}
                      </Link>
                    </div>
                  )
                ))
            )}
          {item.subtitle && (
            <div
              className="items-grid__item-subtitle font-light"
              dangerouslySetInnerHTML={{ __html: item.subtitle }}
            ></div>
          )}
          {item.cta &&
            !!item.cta.length &&
            item.cta.map(
              (cta) =>
                (cta.positionCTA === "bottom" || cta.positionCTA === "all") &&
                (item.skuForWashPass && !cta.link ? (
                  <div className="mt-4">
                    <button
                      className={classNames(
                        `btn-link`,
                        cta.btnColor === "White" && "btn-link--white",
                        cta.btnColor === "Blu" && "btn-link--blue",
                        cta.btnColor === "Black" && "btn-link--black"
                      )}
                      name={item.skuForWashPass}
                      onClick={addItemToCart}
                    >
                      {cta.linkLabel ? (
                        cta.linkLabel
                      ) : (
                        <>
                          <CartIcon className="icon-svg-detail" /> {labelManagerLabels.addToCart}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  cta.link && (
                    <div className="mt-4">
                      <Link
                        to={cta.link}
                        className={classNames(
                          `btn-link`,
                          cta.btnColor === "White" && "btn-link--white",
                          cta.btnColor === "Blu" && "btn-link--blue",
                          cta.btnColor === "Black" && "btn-link--black"
                        )}
                      >
                        {cta.linkLabel || labelManagerLabels.discoverMore}
                      </Link>
                    </div>
                  )
                ))
            )}
        </div>
      </div>
    </div>
  </div>
);

export const ListBlock = React.memo(
  ({
    type,
    list,
    md,
    lg,
    showVideoModal,
    labelManagerLabels,
    config,
    isProduct,
    addItemToCart,
    isFirstModule,
    dataMagento,
    origin,
    dataZuora,
    showLogoImage,
    showLogoSvg,
  }) => {
    return (
      <>
        {isProduct ? (
          <>
            {((isFirstModule && (!showLogoImage || !showLogoSvg)) ||
              (!isFirstModule && (!showLogoImage || !showLogoSvg))) &&
              list
                .filter((item) => item.title || item.image || item.icon)
                .map((item, i) => (
                  <ListingItemPdp
                    item={item}
                    showVideoModal={showVideoModal}
                    labelManagerLabels={labelManagerLabels}
                    key={i}
                    type={type}
                    isFirst={isFirstModule && i === 0 ? true : false}
                    isFourth={(i + 1) % 4 === 0 ? true : false}
                    settings={settings}
                  />
                ))}
            {showLogoSvg &&
              showLogoImage &&
              !isFirstModule &&
              list
                .filter((item) => item.title || item.image || item.icon)
                .map((item, i) => (
                  <StandardItem
                    item={item}
                    md={4}
                    lg={4}
                    showVideoModal={showVideoModal}
                    labelManagerLabels={labelManagerLabels}
                    addItemToCart={addItemToCart}
                    key={i}
                    isProduct={isProduct}
                    dataMagento={dataMagento}
                    origin={origin}
                    dataZuora={dataZuora}
                  />
                ))}
            {isFirstModule &&
              showLogoImage &&
              showLogoSvg &&
              list
                .filter((item) => item.title || item.image || item.icon)
                .map((item, i) => (
                  <ListingItemPdp
                    item={item}
                    showVideoModal={showVideoModal}
                    labelManagerLabels={labelManagerLabels}
                    key={i}
                    type={type}
                    isFirst={false}
                    isFourth={true}
                    settings={settings}
                    showLogoImage={showLogoImage}
                  />
                ))}
          </>
        ) : type === "Listing" || type === "Tripletta with image position" ? (
          list
            .filter((item) => item.title || item.image || item.icon)
            .map((item, i) => (
              <ListingItem
                item={item}
                showVideoModal={showVideoModal}
                labelManagerLabels={labelManagerLabels}
                config={config}
                key={i}
                type={type}
                isProduct={isProduct}
                isFirst={isFirstModule && i === 0 ? true : false}
                isFourth={(i + 1) % 4 === 0 ? true : false}
              />
            ))
        ) : (
          list.map((item, i) => (
            <StandardItem
              item={item}
              md={md}
              lg={lg}
              showVideoModal={showVideoModal}
              labelManagerLabels={labelManagerLabels}
              config={config}
              addItemToCart={addItemToCart}
              key={i}
              isProduct={isProduct}
              dataMagento={dataMagento}
              origin={origin}
              dataZuora={dataZuora}
            />
          ))
        )}
      </>
    );
  }
);

const Podcast = ({ podcastId, iframeId }) => {
  const iframeRef = useRef();
  const [height, setHeight] = useState(465);
  useEffect(() => {
    if (podcastId) {
      const messageListener = (e) => {
        if (e && e.data && e.data[0] && e.data[1] && `${e.data[0]}` === `${iframeId}`) {
          setHeight(e.data[1]);
        }
      };
      window.addEventListener("message", messageListener);
      return () => window.removeEventListener("message", messageListener);
    }
  }, [podcastId, iframeId]);
  return (
    <iframe
      ref={iframeRef}
      src={`https://www.podcastics.com/player/playlist/${iframeId}/?e=${podcastId}`}
      title="podcast"
      frameBorder="0"
      style={{ height: `${height}px`, width: "100%", borderRadius: "10px" }}
    />
  );
};

const ModuleItemsGrid = ({
  module,
  className,
  isProduct,
  isFirstModule,
  dataMagento,
  origin,
  dataZuora,
  showLogoImage,
  showLogoSvg,
  isFirst,
}) => {
  const { showVideoModal } = useContext(YoutubeContext);
  const effectiveList = useMemo(() => formatList(module), [module]);
  const moduleType = useMemo(
    () => (module.moduleType ? module.moduleType?.[0] : ""),
    [module.moduleType]
  );
  let md =
    module.moduleLook?.[0] !== "Inline" && module.sectionTitleFieldSet?.length === 1 ? 12 : 6;
  let lg =
    !module.sectionTitleFieldSet || module.moduleLook?.[0] === "Inline"
      ? 12 / Math.max(1, module.sectionTitleFieldSet.length)
      : null;

  if (module?.moduleType?.[0] === "Dynamic") {
    if (module.imageDimensions?.[0] === "small") {
      md =
        module.moduleLook?.[0] !== "Inline" && module.sectionTitleFieldSet?.length === 1 ? 12 : 4;

      const numberofitems = !(module.sectionTitleFieldSet.length === 4)
        ? 4
        : module.sectionTitleFieldSet.length;

      lg =
        !module.sectionTitleFieldSet || module.moduleLook?.[0] === "Inline"
          ? 12 / Math.max(1, numberofitems)
          : null;
    } else if (module.imageDimensions?.[0] === "medium") {
      md =
        module.moduleLook?.[0] !== "Inline" && module.sectionTitleFieldSet?.length === 1 ? 12 : 6;

      const numberofitems =
        module.sectionTitleFieldSet.length === 2 || module.sectionTitleFieldSet.length === 1
          ? 3
          : module.sectionTitleFieldSet.length;
      lg =
        !module.sectionTitleFieldSet || module.moduleLook?.[0] === "Inline"
          ? 12 / Math.max(1, numberofitems)
          : null;
    }
  }

  const { labelManagerLabels, config } = useStaticQuery(graphql`
    {
      labelManagerLabels: labelManagerLabels {
        discoverMore
        addToCart
        preorder
      }
      config: liferayGeneralConfiguration {
        currency
      }
    }
  `);

  const { addProduct } = useContext(CartContext);
  const currency = config?.currency;
  const addItemToCart = useCallback(
    async (e) => {
      const normalizedProductIds = [];
      try {
        const productId = e.currentTarget.name;
        normalizedProductIds.push(productId);
        const [items = {}] = await queryProductsDetails(normalizedProductIds, currency);
        addProduct(items);
      } catch (err) {
        console.error("Error to retrive product : ", err);
      }
    },
    [addProduct, currency]
  );

  return (
    <>
      {module?.anchorDesktop && (
        <span id={slugify(module.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <section
        className={classNames(
          `section items-grid${className ? ` ${className}` : ""}`,
          isFirst && "mt-4"
        )}
      >
        <div className="container-fluid">
          {module.triplettaTitle && (
            <div className="row">
              <div className="col-12 text-center">
                <h3 className="items-grid__title">{module.triplettaTitle}</h3>
              </div>
            </div>
          )}
        </div>
        <div className="container">
          {module.abstract && (
            <div className="row">
              <div className="col-12">
                <div className="items-grid__subtitle text-center mx-auto mt-4">
                  {Parser(module.abstract, {
                    replace: (node) => {
                      if (node.type === "script" && node.attribs?.src) {
                        const url = new URL(node.attribs.src);
                        if (
                          url.origin === "https://www.podcastics.com" &&
                          /\/player\/playlist\/js\/(\d+)\//.test(url.pathname)
                        ) {
                          const podcastId = url.searchParams.get("e");
                          const matches = /\/player\/playlist\/js\/(\d+)\//.exec(url.pathname);
                          if (matches) {
                            const [, iframeId] = matches;
                            return podcastId && iframeId ? (
                              <Podcast podcastId={podcastId} iframeId={iframeId} />
                            ) : null;
                          }
                        }
                      }
                    },
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
        {effectiveList && (
          <div className="container-fluid">
            <div
              className={classNames(
                !module?.showAsGallery && "row",
                {
                  "justify-content-center": moduleType !== "Listing" && !module?.showAsGallery,
                },
                {
                  "alternate-alignment-right":
                    moduleType === "Tripletta with image position" &&
                    module.imagePositionTripletta?.[0] === "Right",
                },
                {
                  "alternate-alignment-left":
                    moduleType === "Tripletta with image position" &&
                    module.imagePositionTripletta?.[0] !== "Right",
                },
                { "alternate-alignment-left alternate-alignment-left--pdp": isProduct }
              )}
              style={{
                "--image-ratio": `${
                  100 /
                  effectiveList
                    .reduce((acc, item) => (item.image ? acc.concat(item.image) : acc), [])
                    .reduce(
                      (min, item) =>
                        item?.node?.probeInfo?.aspectRatio &&
                        (!min || item?.node?.probeInfo?.aspectRatio < min)
                          ? item.node.probeInfo.aspectRatio
                          : min,
                      undefined
                    )
                }%`,
              }}
            >
              {!module?.showAsGallery ? (
                <ListBlock
                  type={moduleType}
                  list={effectiveList}
                  md={md}
                  lg={lg}
                  showVideoModal={showVideoModal}
                  labelManagerLabels={labelManagerLabels}
                  config={config}
                  isProduct={isProduct}
                  addItemToCart={addItemToCart}
                  isFirstModule={isFirstModule}
                  dataMagento={dataMagento}
                  origin={origin}
                  dataZuora={dataZuora}
                  showLogoImage={showLogoImage}
                  showLogoSvg={showLogoSvg}
                />
              ) : (
                <DynamicAsGallery
                  type={moduleType}
                  list={effectiveList}
                  md={md}
                  lg={lg}
                  showVideoModal={showVideoModal}
                  labelManagerLabels={labelManagerLabels}
                  isProduct={isProduct}
                  addItemToCart={addItemToCart}
                  isFirstModule={isFirstModule}
                  dataMagento={dataMagento}
                  origin={origin}
                  dataZuora={dataZuora}
                  config={config}
                />
              )}
            </div>
          </div>
        )}
      </section>
    </>
  );
};

const ItemsGrid = ({
  data,
  className,
  isProduct,
  dataMagento,
  origin,
  dataZuora,
  showLogoImage,
  showLogoSvg,
  isFirst,
}) => {
  const modules = useMemo(
    () =>
      data.moduleTitleFieldSet.length
        ? data.moduleTitleFieldSet
            .filter((module) => {
              return (
                module?.triplettaTitle ||
                module?.abstract ||
                module.sectionTitleFieldSet?.some(
                  (item) =>
                    item.sectionTitle ||
                    item.sectionImageFieldSet?.sectionImage ||
                    item.fieldsGroup?.icon ||
                    item?.sectionText ||
                    (item.labelFieldSet?.label && item.labelFieldSet?.link)
                )
              );
            })
            .sort(sortPosition)
        : data.moduleTitleFieldSet,
    [data.moduleTitleFieldSet]
  );

  return (
    <>
      {modules.map((module, i) => (
        <ModuleItemsGrid
          module={module}
          className={className}
          key={i}
          isProduct={isProduct}
          isFirstModule={i === 0 ? true : false}
          dataMagento={dataMagento}
          origin={origin}
          dataZuora={dataZuora}
          showLogoImage={showLogoImage}
          showLogoSvg={showLogoSvg}
          isFirst={isFirst}
        />
      ))}
    </>
  );
};

export default ItemsGrid;
export const fragment = graphql`
  fragment DynamicTriplettaListingFragment on LiferayDynamicTriplettaListing {
    id
    liferayFields {
      siteId
      articleId
    }
    moduleTitleFieldSet {
      anchorDesktop
      anchorMobile
      position
      moduleType
      showAsGallery
      moduleLook
      imageDimensions
      imagePositionTripletta
      triplettaTitle: title
      abstract
      sectionTitleFieldSet {
        sectionTitle
        weightSlide
        sectionText
        unaTantum
        monthlyFee
        extraWashFee
        includedWashes
        subTitle
        sectionImageFieldSet {
          sectionImage {
            node {
              gatsbyImageData(width: 800, layout: CONSTRAINED)
              probeInfo {
                aspectRatio
              }
              publicURL
            }
            nodeFull: node {
              gatsbyImageData(layout: FULL_WIDTH)
              publicURL
            }
          }
          altImage
        }
        labelFieldSet {
          label
          link
          color
          positionCta
        }
        video
        skuForWashPass
        ratePlanId
        fieldsGroup {
          icon {
            node {
              publicURL
            }
            value {
              description
            }
          }
          width
          heigth
        }
      }
    }
  }
`;
